var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { cloneDeep, orderBy } from 'lodash';
import moment from 'moment';
import { ELSIcon } from 'components/common';
import ListBox, { LIST_BOX_INDEX } from 'components/common/list-box/ListBox';
import withResizeScreen from 'components/common/with-resize-screen/withResizeScreen';
import { FeatureFlagNames, SORT_DIRECTION_ENUM } from 'constants/app.constant';
import { onKeyDownHandler } from 'helpers/ui.helper';
import { appSelectors } from 'redux/ducks/app';
import { isFeatureEnabled } from 'services/feature-flag.service';
import { cwActions, cwSelectors } from 'reports/cw/redux/ducks/cw';
import { CW_TABLE_SELECTOR } from 'reports/cw/constants/cw.constant';
import { ASSIGNMENT_TYPES, sortDownIcon, sortLabelByType, sortUpIcon } from 'reports/cw/constants/report.constant';
import { querySelectorWrapper } from 'services/method-wrapper.service';
import ListBoxModal from 'reports/had/components/common/list-box-modal/ListBoxModal';
import ReportContent from 'reports/cw/components/common/report-content/ReportContent';
var SORT_BY_LABEL = 'Sort by';
var iconPrefix = 'gizmo';
export var engagementSummarySortOption = [
    {
        name: 'Assignment Completion',
        value: 'assignmentCompletionRate',
        dependedOptions: [
            { name: sortLabelByType.number.desc, value: SORT_DIRECTION_ENUM.desc, icon: sortDownIcon, iconPrefix: iconPrefix },
            { name: sortLabelByType.number.asc, value: SORT_DIRECTION_ENUM.asc, icon: sortUpIcon, iconPrefix: iconPrefix }
        ]
    },
    {
        name: 'Total Assignments Assigned',
        value: 'totalAssignmentsByType',
        dependedOptions: [
            { name: sortLabelByType.number.desc, value: SORT_DIRECTION_ENUM.desc, icon: sortDownIcon, iconPrefix: iconPrefix },
            { name: sortLabelByType.number.asc, value: SORT_DIRECTION_ENUM.asc, icon: sortUpIcon, iconPrefix: iconPrefix }
        ]
    }
];
export var getEngagementSummaryWithAvgLesson = function (engagementSummary) {
    var lessonCardData;
    var typeCardsList = cloneDeep(engagementSummary.typeCardsList);
    var filteredTypeCardsList = typeCardsList.filter(function (typeCard) { return ![ASSIGNMENT_TYPES.ADAPTIVE_LESSON.id, ASSIGNMENT_TYPES.LESSONS.id].includes(typeCard.assignmentType); });
    var lessonEngagement = typeCardsList.find(function (typeCard) { return typeCard.assignmentType === ASSIGNMENT_TYPES.LESSONS.id; });
    var adaptiveLessonEngagement = typeCardsList.find(function (typeCard) { return typeCard.assignmentType === ASSIGNMENT_TYPES.ADAPTIVE_LESSON.id; });
    if (lessonEngagement && adaptiveLessonEngagement) {
        var totalAssignmentsByType = lessonEngagement.totalAssignmentsByType + adaptiveLessonEngagement.totalAssignmentsByType;
        var totalStudents = Math.ceil((lessonEngagement.totalStudents + adaptiveLessonEngagement.totalStudents) / 2);
        var totalStudentsPastDue = Math.ceil((lessonEngagement.totalStudentsPastDue + adaptiveLessonEngagement.totalStudentsPastDue) / 2);
        var assignmentCompletionRate = (lessonEngagement.assignmentCompletionRate * lessonEngagement.totalAssignmentsByType +
            adaptiveLessonEngagement.assignmentCompletionRate * adaptiveLessonEngagement.totalAssignmentsByType) /
            totalAssignmentsByType;
        var lessonAvgTimeSpent = moment.duration(lessonEngagement.avgTimeSpent).as('milliseconds');
        var adaptiveLessonAvgTimeSpent = moment.duration(adaptiveLessonEngagement.avgTimeSpent).as('milliseconds');
        var avgTimeSpentInMilliseconds = lessonAvgTimeSpent > adaptiveLessonAvgTimeSpent ? (lessonAvgTimeSpent - adaptiveLessonAvgTimeSpent) / 2 : (adaptiveLessonAvgTimeSpent - lessonAvgTimeSpent) / 2;
        var avgTimeSpent = moment.duration(Math.round(avgTimeSpentInMilliseconds)).toISOString();
        var percentAvgScore = (lessonEngagement.percentAvgScore + adaptiveLessonEngagement.percentAvgScore) / 2;
        lessonCardData = __assign(__assign({}, lessonEngagement), { totalAssignmentsByType: totalAssignmentsByType, totalStudents: totalStudents, totalStudentsPastDue: totalStudentsPastDue, assignmentCompletionRate: assignmentCompletionRate, avgTimeSpent: avgTimeSpent, percentAvgScore: percentAvgScore });
    }
    else {
        lessonCardData = lessonEngagement || adaptiveLessonEngagement;
    }
    if (lessonCardData) {
        return __assign(__assign({}, engagementSummary), { typeCardsList: __spreadArray([lessonCardData], filteredTypeCardsList, true) });
    }
    return __assign(__assign({}, engagementSummary), { typeCardsList: __spreadArray([], filteredTypeCardsList, true) });
};
export var getSortedEngagementSummary = function (engagementSummary, direction, value) {
    var engagementSummaryMapping = getEngagementSummaryWithAvgLesson(engagementSummary);
    var sortedTypeCardList = orderBy(engagementSummaryMapping.typeCardsList, value, direction);
    return __assign(__assign({}, engagementSummary), { typeCardsList: sortedTypeCardList });
};
var EngagementSummarySort = function (_a) {
    var engagementSummary = _a.engagementSummary, windowMode = _a.windowMode;
    var _b = useState('desc'), sortDirection = _b[0], setSortDirection = _b[1];
    var _c = useState('assignmentCompletionRate'), sortValue = _c[0], setSortValue = _c[1];
    var _d = useState(false), iShowSortModal = _d[0], setIsShowSortModal = _d[1];
    var engagementSummaryCardAndTableSortInteraction = useSelector(function (state) { return cwSelectors.getEngagementSummaryCardAndTableSortInteraction(state); });
    var featureFlags = useSelector(appSelectors.getFeatureFlags);
    var isCWUpdatedUIEnabled = isFeatureEnabled(featureFlags, FeatureFlagNames.CW_UPDATED_UI_ENABLED);
    var dispatch = useDispatch();
    var changeEngagementSummaryListBox = function (option, listIndex) {
        switch (listIndex) {
            case LIST_BOX_INDEX.PARENT:
                setSortValue(option.value);
                break;
            case LIST_BOX_INDEX.DEPENDENT:
                setSortDirection(option.value);
                break;
            default:
                break;
        }
    };
    var handleSortEngagementSummary = function (direction, value) {
        var sortedEngagementSummary = getSortedEngagementSummary(engagementSummary, direction, value);
        dispatch(cwActions.setEngagementSummary(sortedEngagementSummary));
    };
    useEffect(function () {
        handleSortEngagementSummary(sortDirection, sortValue);
    }, [sortDirection, sortValue]);
    useEffect(function () {
        var _a;
        if (engagementSummaryCardAndTableSortInteraction) {
            var field = engagementSummaryCardAndTableSortInteraction.field, cardsSortDirection = engagementSummaryCardAndTableSortInteraction.sortDirection;
            setSortValue(field);
            setSortDirection(cardsSortDirection);
            (_a = querySelectorWrapper(CW_TABLE_SELECTOR.engagementSummary)) === null || _a === void 0 ? void 0 : _a.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [engagementSummaryCardAndTableSortInteraction]);
    var showSortModal = function () {
        setIsShowSortModal(true);
    };
    var hideSortModal = function () {
        setIsShowSortModal(false);
    };
    return (<ReportContent customClass="u-els-padding@mobile u-els-padding-bottom-none c-cw-engagement-summary-sort">
      <h2 className="u-els-margin-bottom@mobile u-els-margin-bottom@tablet">Engagement Summary By Assignment Type</h2>
      {!isCWUpdatedUIEnabled &&
            (windowMode.mobile ? (<div className="c-cw-engagement-summary-sort__mobile-box" onClick={showSortModal} onKeyDown={function (evt) { return onKeyDownHandler(evt, showSortModal); }} role="presentation">
            <div className="u-els-margin-right-1o2">{SORT_BY_LABEL}</div>
            <ELSIcon name="chevron-down" size="1x" align="middle" customClass="u-els-color-extended-blue-8"/>
          </div>) : (<div className="c-cw-engagement-summary-sort__box">
            <div className="c-cw-engagement-summary-sort__box-title">{SORT_BY_LABEL}</div>
            <ListBox inline hasDependent ariaLabel={SORT_BY_LABEL} options={engagementSummarySortOption} value={sortValue} dependedValue={sortDirection} changeHandler={changeEngagementSummaryListBox}/>
          </div>))}
      {!isCWUpdatedUIEnabled && iShowSortModal && (<ListBoxModal hasDependent header={SORT_BY_LABEL} headerIcon="card-expand" options={engagementSummarySortOption} value={sortValue} dependedValue={sortDirection} changeHandler={changeEngagementSummaryListBox} closeHandler={hideSortModal}/>)}
    </ReportContent>);
};
export { EngagementSummarySort as EngagementSummarySortBase };
export default withResizeScreen(EngagementSummarySort);
